import { gql, useMutation, useQuery } from '@apollo/client';
import { Callout } from '@radix-ui/themes';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Pagination } from '../elements/Pagination';
import Title from '../elements/Title';
import useSearchParams from '../hooks/useSearchParams';
import ListJobs from '../jobs/ListJobs';
import { Error } from './Error';
import PageHeader from './PageHeader';

const JOBS_QUERY = gql`
  query JOBS($limit: Int, $skip: Int) {
    allJobs(sort: { field: active, order: DESC }, limit: $limit, skip: $skip) {
      totalCount
      nodes {
        title
        id
        company
        active
        createdAt
        threshold
        pinned
        location {
          id
          name
          city
          country
        }
        externalRef {
          externalId
          source
        }
        new: applications(filter: { stage: { equals: null } }) {
          totalCount
        }
        leads: applications(filter: { stage: { equals: 2 } }) {
          totalCount
        }
        accepted: applications(filter: { stage: { equals: 3 } }) {
          totalCount
        }
        hired: applications(filter: { stage: { equals: 4 } }) {
          totalCount
        }
        notHired: applications(filter: { stage: { equals: 5 } }) {
          totalCount
        }

        total: applications {
          totalCount
        }
      }
    }
  }
`;

const EDIT_JOB = gql`
  mutation JobSetActive($jobId: String!, $jobActive: Boolean!) {
    jobSetActive(jobId: $jobId, jobActive: $jobActive) {
      __typename
      id
      active
    }
  }
`;

export const Jobs = () => {
  const { t } = useTranslation();
  const JOBS_PER_PAGE = 15;

  const query = useSearchParams();
  const queryPage = parseInt(query.get('page') || '1') || 1;

  const [currentPage, setCurrentPage] = useState(queryPage);
  const [totalPages, setTotalPages] = useState<number>(0);

  const skip = JOBS_PER_PAGE * (currentPage - 1);

  const navigate = useNavigate();

  const { data, error, loading } = useQuery(JOBS_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    variables: { skip: skip, limit: JOBS_PER_PAGE },
  });

  const { data: pinnedJobsData } = useQuery(
    gql`
      query pinnedJobs {
        pinnedJobs: viewer {
          id
          pinnedJobs {
            externalRef {
              externalId
              source
            }
            title
            id
            company
            active
            createdAt
            threshold
            pinned
            location {
              id
              name
              city
              address
            }
            new: applications(filter: { stage: { equals: null } }) {
              totalCount
            }
            leads: applications(filter: { stage: { equals: 2 } }) {
              totalCount
            }
            accepted: applications(filter: { stage: { equals: 3 } }) {
              totalCount
            }
            hired: applications(filter: { stage: { equals: 4 } }) {
              totalCount
            }
            notHired: applications(filter: { stage: { equals: 5 } }) {
              totalCount
            }

            total: applications {
              totalCount
            }
          }
        }
      }
    `,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    navigate({
      pathname: '/jobs',
      search: `?page=${page}`,
    });
  };

  useEffect(() => {
    if (data?.allJobs.totalCount !== undefined) {
      const numPages = Math.ceil(data?.allJobs.totalCount / JOBS_PER_PAGE);
      setTotalPages(numPages);
    }
  }, [data?.allJobs.totalCount]);

  const [setJobActiveStatus, { error: mutationError }] = useMutation(EDIT_JOB, {
    update: (cache, { data: { jobSetActive } }) => {
      const cacheId = cache.identify(jobSetActive);

      cache.modify({
        id: cacheId,
        broadcast: false,
        fields: {
          active() {
            return jobSetActive.active;
          },
        },
      });
    },
  });

  const toggleJobActive = (job: { id: string; active: boolean; title: string }) => {
    setJobActiveStatus({
      variables: {
        jobId: job.id,
        jobActive: !job.active,
      },
      optimisticResponse: {
        jobSetActive: {
          __typename: 'Job',
          id: job.id,
          active: !job.active,
        },
      },
    }).catch((err) => {
      toast.error(t('jobs.toast.statusChangeFailed', { jobTitle: job.title }), {
        duration: 6000,
        description: err.message,
      });
      console.error(err.message);
    });
  };

  return (
    <div>
      <Title title={t('jobs.pageTitle')} />

      <PageHeader className="">
        <PageHeader.Title>{t('jobs.heading')}</PageHeader.Title>
      </PageHeader>
      <div className="border-grey-top pt-4 p-1"></div>

      {pinnedJobsData?.pinnedJobs?.pinnedJobs?.length > 0 && (
        <>
          <h2>{t('jobs.pinnedJobsHeading')}</h2>
          <ListJobs onJobActiveToggle={toggleJobActive} jobs={pinnedJobsData.pinnedJobs.pinnedJobs} />
        </>
      )}
      <div>
        {Error(error)}
        {mutationError && (
          <Callout.Root color="red">
            <Callout.Text className="mb-0">{t('jobs.mutationError.message')}</Callout.Text>
          </Callout.Root>
        )}

        <ListJobs
          onJobActiveToggle={toggleJobActive}
          jobs={data && !loading ? data.allJobs.nodes : null}
          loading={loading}
        />
      </div>
      {!loading && totalPages > 0 && (
        <div className="flex justify-center mb-4">
          <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  );
};
